import { useState, type FunctionComponent } from "react";
import type { ActionCardStatus } from "@noa/api-core";
import { ActionCardButton } from "./action-card-button";
import { ActionCardStatusDisplay } from "./action-card-status";
import { getActionConfig } from "./registry";

interface ActionCardProps {
  actionType: string;
  label: string;
  data: Record<string, unknown>;
}

export const ActionCard: FunctionComponent<ActionCardProps> = ({ actionType, label, data }) => {
  const [status, setStatus] = useState<ActionCardStatus>("idle");
  const [formData, setFormData] = useState(data);
  const [error, setError] = useState<string>();

  const config = getActionConfig(actionType);

  if (!config) {
    console.warn(`[ActionCard] Unknown action type: ${actionType}`);
    return null;
  }

  const FormComponent = config.formComponent;

  const handleSubmit = async () => {
    setStatus("submitting");
    setError(undefined);
    try {
      await config.submitHandler(formData);
      setStatus("success");
    } catch (e) {
      setError(e instanceof Error ? e.message : "Une erreur est survenue");
      setStatus("error");
    }
  };

  const handleRetry = () => {
    setStatus("editing");
  };

  return (
    <div className="action-card" data-testid={`action-card-${actionType}`} data-status={status}>
      {status === "idle" && <ActionCardButton label={label} onClick={() => setStatus("editing")} />}

      {(status === "editing" || status === "submitting") && (
        <div className="action-card-form" data-testid="action-card-form">
          <FormComponent
            data={formData}
            onChange={setFormData}
            onSubmit={handleSubmit}
            onCancel={() => setStatus("idle")}
            isSubmitting={status === "submitting"}
          />
        </div>
      )}

      {(status === "success" || status === "error") && (
        <ActionCardStatusDisplay
          status={status}
          error={error}
          onRetry={handleRetry}
          successMessageKey={config.successMessageKey}
          term={typeof formData.term === "string" ? formData.term : undefined}
        />
      )}
    </div>
  );
};
